import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import ModalEstraiRighe, { RigaEstratta } from './ModalEstraiRighe';
import ModalGeneraDdt from '../ddt/ModalGeneraDdt';
import PrintModal from '../../../../components/PrintModal';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatQuantity } from '../../../../utils/formatters';
import { usePermesso } from '../../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './SchedaDistinta.api';
import './SchedaDistinta.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoDistinta = 'MERCE' | 'LAVORAZIONI';
type AddebitoDistinta = 'NOSTRO' | 'CLIENTE';
type StatoDistinta = 'BOZZA' | 'CONFERMATA';

interface RigaDistinta {
  id: number;
  ordinePos: number;
  rigaOrdineId: number | null;
  componenteId: number | null;
  articoloId: number | null;
  descrizione: string;
  quantita: number | string;
  unitaMisura: string | null;
  note: string | null;
  articolo: { id: number; codice: string; descrizioneBase: string; tipologia: string } | null;
  rigaOrdine: { id: number; ordinePos: number; descrizione: string } | null;
}

interface LogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface Distinta {
  id: number;
  commessaId: number | null;
  ordineId: number;
  numero: number;
  tipo: TipoDistinta;
  addebito: AddebitoDistinta;
  descrizione: string | null;
  stato: StatoDistinta;
  confermataAt: string | null;
  createdAt: string;
  ordine: {
    id: number;
    numero: string;
    stato: string;
    flussoCommerciale: string;
    societaId: number;
  };
  commessa: {
    id: number;
    numero: string;
    descrizione: string | null;
    clienteRagioneSociale: string | null;
  } | null;
  confermataDa: { id: number; nome: string; cognome?: string | null } | null;
  createdBy: { id: number; nome: string; cognome?: string | null } | null;
  righe: RigaDistinta[];
  logs: LogEntry[];
}

// Riga in editing locale (quantità come stringa per l'input)
interface RigaLocale {
  key: string;
  rigaOrdineId: number | null;
  componenteId: number | null;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  quantita: string;
  unitaMisura: string | null;
  note: string;
  rigaOrdinePos: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const ADDEBITO_LABEL: Record<AddebitoDistinta, string> = {
  NOSTRO: 'Nostro carico',
  CLIENTE: 'Carico cliente',
};

function formatDateTimeIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

function nomeUtente(u: { nome: string; cognome?: string | null } | null): string | null {
  if (!u) return null;
  return `${u.nome || ''} ${u.cognome || ''}`.trim() || null;
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

let rigaKeyCounter = 0;
function nuovaKey(): string {
  rigaKeyCounter += 1;
  return `riga-${Date.now().toString(36)}-${rigaKeyCounter}`;
}

function rigaServerToLocale(r: RigaDistinta): RigaLocale {
  return {
    key: `srv-${r.id}`,
    rigaOrdineId: r.rigaOrdineId,
    componenteId: r.componenteId,
    articoloId: r.articoloId,
    codice: r.articolo?.codice ?? null,
    descrizione: r.descrizione,
    quantita: String(r.quantita ?? '').replace('.', ','),
    unitaMisura: r.unitaMisura,
    note: r.note || '',
    rigaOrdinePos: r.rigaOrdine?.ordinePos ?? null,
  };
}

function formatDataIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// ── Card DDT collegati (DDT su cantiere dell'ordine della distinta) ──────────

type StatoDdt = 'BOZZA' | 'EMESSO' | 'ANNULLATO';

interface DdtCollegato {
  id: number;
  tipo: 'VENDITA' | 'CANTIERE';
  stato: StatoDdt;
  numero: string | null;
  dataEmissione: string | null;
  _count: { righe: number };
}

const STATO_DDT_CFG: Record<StatoDdt, { label: string; cls: string }> = {
  BOZZA: { label: 'Bozza', cls: 'bop-badge-neutral' },
  EMESSO: { label: 'Emesso', cls: 'bop-badge-info' },
  ANNULLATO: { label: 'Annullato', cls: 'bop-badge-danger' },
};

function CardDdtCollegati({ ordineId, societaId }: { ordineId: number; societaId: number }) {
  const navigate = useNavigate();
  const [ddt, setDdt] = useState<DdtCollegato[]>([]);
  const [loadingDdt, setLoadingDdt] = useState(true);

  useEffect(() => {
    let attivo = true;
    (async () => {
      setLoadingDdt(true);
      try {
        // @api GET /api/ddt?societaId&ordineId -> {"success":true,"data":[{"id":"number","tipo":"VENDITA|CANTIERE","stato":"BOZZA|EMESSO|ANNULLATO","numero":"string|null","dataEmissione":"string|null","_count":{"righe":"number"}}]}
        const res = await apiFetch<{ success: boolean; data?: DdtCollegato[]; error?: unknown }>(
          `/api/ddt?societaId=${societaId}&ordineId=${ordineId}`
        );
        if (!attivo) return;
        if (!res.success) {
          showToast(estraiErrore({ payload: res }, 'Errore nel caricamento dei DDT'), 'error');
          setDdt([]);
        } else {
          setDdt((res.data ?? []).filter(x => x.tipo === 'CANTIERE'));
        }
      } catch (err: unknown) {
        if (attivo) {
          showToast(estraiErrore(err, 'Errore nel caricamento dei DDT'), 'error');
          setDdt([]);
        }
      } finally {
        if (attivo) setLoadingDdt(false);
      }
    })();
    return () => { attivo = false; };
  }, [ordineId, societaId]);

  return (
    <div className="bop-card">
      <div className="bop-card__header">
        <h2 className="bop-card__title">DDT collegati</h2>
      </div>
      <div className="bop-card__body">
        {loadingDdt ? (
          <div className="bop-empty-state">Caricamento DDT…</div>
        ) : ddt.length === 0 ? (
          <div className="bop-empty-state">Nessun DDT su cantiere per l'ordine di questa distinta.</div>
        ) : (
          <table className="bop-table bop-table--clickable">
            <thead>
              <tr>
                <th>Numero</th>
                <th>Stato</th>
                <th>Data</th>
                <th className="bop-text-right">Righe</th>
              </tr>
            </thead>
            <tbody>
              {ddt.map(x => {
                const statoCfg = STATO_DDT_CFG[x.stato] ?? { label: x.stato, cls: 'bop-badge-neutral' };
                return (
                  <tr key={x.id} onClick={() => navigate(`/commerciale/attivo/ddt/${x.id}`)}>
                    <td>{x.numero ?? <span className="bop-badge bop-badge-neutral">Bozza</span>}</td>
                    <td><span className={`bop-badge ${statoCfg.cls}`}>{statoCfg.label}</span></td>
                    <td>{formatDataIT(x.dataEmissione)}</td>
                    <td className="bop-text-right">{x._count?.righe ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Campo label/valore in sola lettura
function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="dist-scheda__campo">
      <span className="dist-scheda__campo-label">{label}</span>
      <span className="dist-scheda__campo-value">
        {value === null || value === undefined || value === '' ? (
          <span className="bop-text-muted">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function SchedaDistinta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermesso();

  const [distinta, setDistinta] = useState<Distinta | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  // Editing info (descrizione + addebito)
  const [descrizione, setDescrizione] = useState('');
  const [addebito, setAddebito] = useState<AddebitoDistinta>('NOSTRO');
  const [infoSnapshot, setInfoSnapshot] = useState('');

  // Editing righe
  const [righe, setRighe] = useState<RigaLocale[]>([]);
  const [righeSnapshot, setRigheSnapshot] = useState('');

  const [modalEstraiAperta, setModalEstraiAperta] = useState(false);
  const [modalDdtAperta, setModalDdtAperta] = useState(false);
  const [salvandoInfo, setSalvandoInfo] = useState(false);
  const [salvandoRighe, setSalvandoRighe] = useState(false);
  const [azioneInCorso, setAzioneInCorso] = useState(false);

  const etichetta = distinta ? `${distinta.ordine.numero}-D${distinta.numero}` : '';
  const isBozza = distinta?.stato === 'BOZZA';
  const canEdit = can('commerciale.distinte', 'edit');
  const canDelete = can('commerciale.distinte', 'delete');
  const canDdtEdit = can('commerciale.ddt', 'edit');
  const canDdtView = can('commerciale.ddt', 'view');
  const editabile = isBozza && canEdit;

  useBloccoRecordContesto({
    recordSocietaId: distinta?.ordine.societaId,
    label: distinta ? `Distinta ${etichetta}` : 'Distinta',
  });

  const infoDirty =
    infoSnapshot !== '' && JSON.stringify({ descrizione, addebito }) !== infoSnapshot;
  const righeDirty = righeSnapshot !== '' && JSON.stringify(righe) !== righeSnapshot;
  useDirtyForm(editabile && (infoDirty || righeDirty), distinta ? `Distinta ${etichetta}` : 'Distinta');

  // Stampa / invio: motore di stampa, modulo DISTINTA
  const [modalStampa, setModalStampa] = useState(false);
  const apriStampa = () => {
    if (editabile && (infoDirty || righeDirty)) {
      showToast('Salva le modifiche prima di stampare la distinta', 'error');
      return;
    }
    setModalStampa(true);
  };

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      // @api GET /api/distinte/:id -> {"success":true,"data":{"id":"number","commessaId":"number","ordineId":"number","numero":"number","tipo":"MERCE|LAVORAZIONI","addebito":"NOSTRO|CLIENTE","descrizione":"string","stato":"BOZZA|CONFERMATA","confermataAt":"string","createdAt":"string","ordine":{"id":"number","numero":"string","stato":"string","flussoCommerciale":"string","societaId":"number"},"commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"confermataDa":{"id":"number","nome":"string","cognome":"string"},"createdBy":{"id":"number","nome":"string","cognome":"string"},"righe":[{"id":"number","ordinePos":"number","rigaOrdineId":"number","componenteId":"number","articoloId":"number","descrizione":"string","quantita":"number","unitaMisura":"string","note":"string","articolo":{"id":"number","codice":"string","descrizioneBase":"string","tipologia":"string"},"rigaOrdine":{"id":"number","ordinePos":"number","descrizione":"string"}}],"logs":[{"id":"number","utenteName":"string","azione":"string","dettaglio":"string","createdAt":"string"}]}}
      const r = await apiFetch<{ success: boolean; data?: Distinta; error?: string }>(`/api/distinte/${id}`);
      if (!r.success || !r.data) {
        setErrore(typeof r.error === 'string' ? r.error : 'Distinta non trovata');
        setDistinta(null);
        return;
      }
      const d = r.data;
      setDistinta(d);
      setDescrizione(d.descrizione || '');
      setAddebito(d.addebito);
      setInfoSnapshot(JSON.stringify({ descrizione: d.descrizione || '', addebito: d.addebito }));
      const righeLocali = (d.righe || []).map(rigaServerToLocale);
      setRighe(righeLocali);
      setRigheSnapshot(JSON.stringify(righeLocali));
    } catch (err: unknown) {
      setErrore(estraiErrore(err, 'Errore nel caricamento della distinta'));
      setDistinta(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  // ── Azioni header ───────────────────────────────────────────────────────────

  const conferma = async () => {
    if (!distinta) return;
    if (infoDirty || righeDirty) {
      showToast('Salva le modifiche prima di confermare la distinta', 'error');
      return;
    }
    if (!window.confirm(`Confermare la distinta ${etichetta}? Dopo la conferma le righe non saranno più modificabili.`)) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/distinte/:id/conferma -> {"success":true,"data":{"stato":"CONFERMATA","confermataAt":"string"}}
      await apiFetch<{ success: boolean; data?: { stato: string; confermataAt: string } }>(
        `/api/distinte/${distinta.id}/conferma`,
        { method: 'POST' }
      );
      showToast(`Distinta ${etichetta} confermata`, 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nella conferma della distinta'), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const riportaInBozza = async () => {
    if (!distinta) return;
    if (!window.confirm(`Riportare in bozza la distinta ${etichetta}?`)) return;
    setAzioneInCorso(true);
    try {
      // @api POST /api/distinte/:id/riporta-in-bozza -> {"success":true,"data":{"stato":"BOZZA"}}
      await apiFetch<{ success: boolean; data?: { stato: string } }>(
        `/api/distinte/${distinta.id}/riporta-in-bozza`,
        { method: 'POST' }
      );
      showToast(`Distinta ${etichetta} riportata in bozza`, 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel riporto in bozza'), 'error');
    } finally {
      setAzioneInCorso(false);
    }
  };

  const elimina = async () => {
    if (!distinta) return;
    if (!window.confirm(`Eliminare definitivamente la distinta ${etichetta}?`)) return;
    setAzioneInCorso(true);
    try {
      // @api DELETE /api/distinte/:id -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/distinte/${distinta.id}`, { method: 'DELETE' });
      showToast(`Distinta ${etichetta} eliminata`, 'success');
      navigate('/commerciale/attivo/distinte');
    } catch (err: unknown) {
      showToast(estraiErrore(err, "Errore nell'eliminazione della distinta"), 'error');
      setAzioneInCorso(false);
    }
  };

  // ── Salvataggio informazioni ────────────────────────────────────────────────

  const salvaInfo = async () => {
    if (!distinta) return;
    setSalvandoInfo(true);
    try {
      // @api PUT /api/distinte/:id {"descrizione":"string","addebito":"NOSTRO|CLIENTE"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/distinte/${distinta.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          descrizione: descrizione.trim() || null,
          addebito,
        }),
      });
      showToast('Informazioni distinta salvate', 'success');
      setInfoSnapshot(JSON.stringify({ descrizione, addebito }));
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio'), 'error');
    } finally {
      setSalvandoInfo(false);
    }
  };

  // ── Editing righe ───────────────────────────────────────────────────────────

  const setCampoRiga = (key: string, campo: 'quantita' | 'note', val: string) => {
    setRighe(prev => prev.map(r => (r.key === key ? { ...r, [campo]: val } : r)));
  };

  const rimuoviRiga = (key: string) => {
    setRighe(prev => prev.filter(r => r.key !== key));
  };

  const aggiungiRigheDaModal = (estratte: RigaEstratta[]) => {
    setRighe(prev => [
      ...prev,
      ...estratte.map(e => ({
        key: nuovaKey(),
        rigaOrdineId: e.rigaOrdineId,
        componenteId: e.componenteId,
        articoloId: e.articoloId,
        codice: null,
        descrizione: e.descrizione,
        quantita: String(e.quantita).replace('.', ','),
        unitaMisura: e.unitaMisura,
        note: e.note || '',
        rigaOrdinePos: null,
      })),
    ]);
    setModalEstraiAperta(false);
  };

  const salvaRighe = async () => {
    if (!distinta) return;
    const payload: Array<{
      rigaOrdineId: number | null;
      componenteId: number | null;
      articoloId: number | null;
      descrizione: string;
      quantita: number;
      unitaMisura: string | null;
      note: string | null;
    }> = [];
    for (const r of righe) {
      if (!r.descrizione.trim()) {
        showToast('Ogni riga deve avere una descrizione', 'error');
        return;
      }
      const q = Number(r.quantita.replace(',', '.'));
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità non valida per la riga "${r.descrizione}"`, 'error');
        return;
      }
      payload.push({
        rigaOrdineId: r.rigaOrdineId,
        componenteId: r.componenteId,
        articoloId: r.articoloId,
        descrizione: r.descrizione.trim(),
        quantita: q,
        unitaMisura: r.unitaMisura,
        note: r.note.trim() || null,
      });
    }
    setSalvandoRighe(true);
    try {
      // @api PUT /api/distinte/:id {"righe":[{"rigaOrdineId":"number|null","componenteId":"number|null","articoloId":"number|null","descrizione":"string","quantita":"number","unitaMisura":"string|null","note":"string|null"}]} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/distinte/${distinta.id}`, {
        method: 'PUT',
        body: JSON.stringify({ righe: payload }),
      });
      showToast('Righe distinta salvate', 'success');
      await carica();
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel salvataggio delle righe'), 'error');
    } finally {
      setSalvandoRighe(false);
    }
  };

  const renderOrigine = (r: RigaLocale): React.ReactNode => {
    if (r.rigaOrdineId === null) return 'Libera';
    return r.rigaOrdinePos !== null ? `Riga ordine #${r.rigaOrdinePos}` : 'Riga ordine';
  };

  const quantitaConfermata = (r: RigaLocale): string => {
    const q = Number(r.quantita.replace(',', '.'));
    return Number.isFinite(q) ? formatQuantity(q) : r.quantita;
  };

  const badges = useMemo(() => {
    if (!distinta) return null;
    return (
      <div className="dist-scheda__badges">
        {distinta.tipo === 'MERCE'
          ? <span className="bop-badge bop-badge-info">Merce</span>
          : <span className="bop-badge dist-scheda__badge-lavorazioni">Lavorazioni</span>}
        {distinta.addebito === 'NOSTRO'
          ? <span className="bop-badge bop-badge-neutral">Nostro carico</span>
          : <span className="bop-badge bop-badge-warning">Carico cliente</span>}
        {distinta.stato === 'BOZZA'
          ? <span className="bop-badge bop-badge-neutral">Bozza</span>
          : <span className="bop-badge bop-badge-success">Confermata</span>}
      </div>
    );
  }, [distinta]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="dist-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento distinta…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errore || !distinta) {
    return (
      <Layout>
        <div className="dist-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Distinta non disponibile</h3>
              <p className="bop-empty-state__text">{errore || 'Distinta non trovata.'}</p>
              <BopBackLink to="/commerciale/attivo/distinte" label="Torna alle distinte" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const d = distinta;

  return (
    <Layout>
      <div className="dist-scheda">
        <BopBackLink to="/commerciale/attivo/distinte" label="Torna alle distinte" />

        {/* Header */}
        <header className="dist-scheda__header">
          <div className="dist-scheda__breadcrumb">
            Commerciale ›{' '}
            <Link to="/commerciale/attivo/distinte">Distinte</Link> › {etichetta}
          </div>
          <div className="dist-scheda__header-row">
            <div className="dist-scheda__header-left">
              <h1 className="bop-page-title">Distinta {etichetta}</h1>
              {badges}
            </div>
            <div className="dist-scheda__header-actions">
              <button
                type="button"
                className="bop-btn bop-btn-secondary"
                onClick={apriStampa}
                title="Anteprima, PDF o invio email della distinta con il template di Impostazioni → Report"
              >
                Stampa / Invia
              </button>
              {isBozza && canEdit && (
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={conferma}
                  disabled={azioneInCorso}
                >
                  Conferma distinta
                </button>
              )}
              {isBozza && canDelete && (
                <button
                  className="bop-btn bop-btn-danger"
                  onClick={elimina}
                  disabled={azioneInCorso}
                >
                  Elimina
                </button>
              )}
              {!isBozza && canEdit && (
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={riportaInBozza}
                  disabled={azioneInCorso}
                >
                  Riporta in bozza
                </button>
              )}
              {d.tipo === 'MERCE' && d.stato === 'CONFERMATA' && canDdtEdit && (
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={() => setModalDdtAperta(true)}
                  disabled={azioneInCorso}
                  title="Genera un DDT su cantiere dalle righe di questa distinta"
                >
                  Genera DDT cantiere
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Informazioni */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Informazioni</h2>
            {editabile && infoDirty && (
              <button
                className="bop-btn bop-btn-primary bop-btn-sm"
                onClick={salvaInfo}
                disabled={salvandoInfo}
              >
                {salvandoInfo ? 'Salvataggio…' : 'Salva'}
              </button>
            )}
          </div>
          <div className="bop-card__body">
            <div className="dist-scheda__grid">
              <Campo
                label="Ordine"
                value={
                  <Link to={`/commerciale/attivo/ordini/${d.ordine.id}`}>{d.ordine.numero}</Link>
                }
              />
              <Campo
                label="Commessa"
                value={
                  d.commessa ? (
                    <Link to={`/commerciale/commesse/${d.commessa.id}`}>
                      {d.commessa.numero}
                      {d.commessa.descrizione ? ` — ${d.commessa.descrizione}` : ''}
                    </Link>
                  ) : null
                }
              />
              <Campo label="Cliente" value={d.commessa?.clienteRagioneSociale || null} />

              {editabile ? (
                <div className="dist-scheda__campo">
                  <span className="dist-scheda__campo-label">Descrizione</span>
                  <input
                    className="bop-input"
                    value={descrizione}
                    onChange={e => setDescrizione(e.target.value)}
                    placeholder="Descrizione distinta"
                  />
                </div>
              ) : (
                <Campo label="Descrizione" value={d.descrizione} />
              )}

              {editabile ? (
                <div className="dist-scheda__campo">
                  <span className="dist-scheda__campo-label">Addebito</span>
                  <select
                    className="bop-select"
                    value={addebito}
                    onChange={e => setAddebito(e.target.value as AddebitoDistinta)}
                  >
                    <option value="NOSTRO">Nostro carico</option>
                    <option value="CLIENTE">Carico cliente</option>
                  </select>
                </div>
              ) : (
                <Campo label="Addebito" value={ADDEBITO_LABEL[d.addebito]} />
              )}

              <Campo
                label="Creata da / il"
                value={
                  nomeUtente(d.createdBy) || d.createdAt
                    ? `${nomeUtente(d.createdBy) || '—'} · ${formatDateTimeIT(d.createdAt)}`
                    : null
                }
              />
              <Campo
                label="Confermata da / il"
                value={
                  d.confermataAt
                    ? `${nomeUtente(d.confermataDa) || '—'} · ${formatDateTimeIT(d.confermataAt)}`
                    : null
                }
              />
            </div>
          </div>
        </div>

        {/* Righe */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe</h2>
            {editabile && (
              <div className="dist-scheda__righe-actions">
                <button
                  className="bop-btn bop-btn-secondary bop-btn-sm"
                  onClick={() => setModalEstraiAperta(true)}
                >
                  + Aggiungi righe da ordine
                </button>
                <button
                  className="bop-btn bop-btn-primary bop-btn-sm"
                  onClick={salvaRighe}
                  disabled={salvandoRighe || !righeDirty}
                >
                  {salvandoRighe ? 'Salvataggio…' : 'Salva righe'}
                </button>
              </div>
            )}
          </div>
          <div className="bop-card__body">
            {righe.length === 0 ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">
                  Nessuna riga in distinta.
                  {editabile ? ' Usa "+ Aggiungi righe da ordine" per estrarre le righe.' : ''}
                </p>
              </div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>U.M.</th>
                    <th className="bop-text-right">Quantità</th>
                    <th>Origine</th>
                    <th>Note</th>
                    {editabile && <th className="bop-table__th--actions"></th>}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, i) => (
                    <tr key={r.key}>
                      <td>{i + 1}</td>
                      <td>{r.codice || <span className="bop-text-muted">—</span>}</td>
                      <td>{r.descrizione}</td>
                      <td>{r.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                      <td className="bop-text-right">
                        {editabile ? (
                          <input
                            className="bop-input dist-scheda__qta-input"
                            value={r.quantita}
                            onChange={e => setCampoRiga(r.key, 'quantita', e.target.value)}
                          />
                        ) : (
                          quantitaConfermata(r)
                        )}
                      </td>
                      <td>{renderOrigine(r)}</td>
                      <td>
                        {editabile ? (
                          <input
                            className="bop-input dist-scheda__note-input"
                            value={r.note}
                            onChange={e => setCampoRiga(r.key, 'note', e.target.value)}
                            placeholder="Note"
                          />
                        ) : (
                          r.note || <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      {editabile && (
                        <td className="bop-table__td--actions">
                          <button
                            className="bop-btn bop-btn-danger bop-btn-sm"
                            onClick={() => rimuoviRiga(r.key)}
                          >
                            Rimuovi
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* DDT collegati (DDT su cantiere dell'ordine) */}
        {canDdtView && d.stato === 'CONFERMATA' && (
          <CardDdtCollegati ordineId={d.ordineId} societaId={d.ordine.societaId} />
        )}

        {/* Log attività */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Log attività</h2>
          </div>
          <div className="bop-card__body">
            {(d.logs || []).length === 0 ? (
              <div className="bop-empty-state">Nessuna voce di log.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data/ora</th>
                    <th>Utente</th>
                    <th>Azione</th>
                    <th>Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {d.logs.map(l => (
                    <tr key={l.id}>
                      <td>{formatDateTimeIT(l.createdAt)}</td>
                      <td>{l.utenteName}</td>
                      <td>{l.azione}</td>
                      <td>{l.dettaglio || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {modalEstraiAperta && (
          <ModalEstraiRighe
            ordineId={d.ordineId}
            tipo={d.tipo}
            onClose={() => setModalEstraiAperta(false)}
            onConfirm={aggiungiRigheDaModal}
          />
        )}

        {modalDdtAperta && (
          <ModalGeneraDdt
            tipo="CANTIERE"
            distintaId={d.id}
            onClose={() => setModalDdtAperta(false)}
            onCreated={(ddtId) => navigate(`/commerciale/attivo/ddt/${ddtId}`)}
          />
        )}

        {modalStampa && (
          <PrintModal
            societaId={d.ordine.societaId}
            entitaType="distinta"
            entitaId={d.id}
            entitaLabel={`Distinta ${etichetta}`}
            onClose={() => setModalStampa(false)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
